import {AxiosHeaders, type AxiosResponse, isAxiosError} from 'axios';

import {HttpStatus} from '_enums/HttpStatus';

import {values} from '_utils/obj';
import {toastError, toastInfo} from "_components/ToastProvider";
import {ErrorCode, ErrorCodeInfo} from "_enums/ErrorCode";

export {isAxiosError};

export async function unwrap<T>(promise: any): Promise<T> {
    return (await promise)?.data;
}

export async function unwrapCollection<T>(promise: any): Promise<T> {
    return ((await unwrap(promise)) as any)?.data;
}

export function toastRequestError(error: unknown, description?: string): unknown {
    if (!isAxiosError(error) || !error.response) {
        toastError(description ?? (error instanceof Error ? error.message : 'Erro desconhecido.'));

        return;
    }

    const {status, data} = error.response;

    if (status === HttpStatus.VALIDATION_ERROR) {
        const errors = values<{ field: string, message: string }>(data.errors);
        const duration = ([data.message, ...errors.flat(1)].join(' ').split(' ').length / 100) * 60e3;

        toastInfo(<ul>
                {errors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                ))}
            </ul>,
            "⚠️ Erro de validação",
            duration);
        return;
    }
    if (status === HttpStatus.BAD_REQUEST) {
        const message = data.message || "Erro 400";
        toastInfo(message, "️️⚠️ Ação não concluída");
        return;
    }
    if (status === 429) {
        const retryAfter = parseRetryAfter(error.response);
        const minutes = ~~(retryAfter / 60);
        const time = minutes ? `${minutes} minutos` : `${retryAfter} segundos`;
        const duration = Math.max(10, retryAfter) * 1e3;

        toastError(
            `Você atingiu o limite de ações por minuto. Por favor, aguarde mais ${time} antes de tentar novamente.`
        );

        return;
    }

    if (status === 401) {
        toastError(data.message || 'Não conseguimos lhe identificar.', "🔒 Não autenticado");
        return;
    }

    if (status in ErrorCodeInfo) {
        const info = ErrorCodeInfo[status as ErrorCode];

        toastError(data.message || info.label, info.title);

        return;
    }

    toastError(data.message || `Erro ${status}.`);
}

export function parseRetryAfter(response: AxiosResponse): number {
    const {headers} = response;

    if (headers instanceof AxiosHeaders) {
        return +(headers.get('Retry-After') ?? headers.get('X-Retry-After') ?? 0);
    }

    return 0;
}

export function isFormData(value: any): value is FormData {
    return String(value) === '[object FormData]';
}
